import { ALL_SITES } from '@tempus/trials-types';

import type { SiteWithId, UserSiteMap } from './types';

type SelectedSite = Pick<SiteWithId, 'id' | 'name'> | null | undefined;

/** True when the user selected the aggregate "All Sites" view (not a single institution). */
export function isAllSitesSelected(selectedUserSite: SelectedSite): boolean {
  return selectedUserSite?.name === ALL_SITES;
}

/** True when a single institution is selected (excludes All Sites, including when id is the ALL_SITES sentinel). */
export function isSpecificSiteSelected(selectedUserSite: SelectedSite): boolean {
  const id = selectedUserSite?.id;
  return Boolean(id && id !== ALL_SITES);
}

/**
 * Resolves the site id sent to trials APIs from the selected site in Redux.
 * Returns null when no site context should be sent (e.g. nothing selected).
 */
export function getApiSiteId(selectedUserSite: SelectedSite): string | null {
  if (!selectedUserSite) {
    return null;
  }

  if (isSpecificSiteSelected(selectedUserSite)) {
    return selectedUserSite.id;
  }

  if (isAllSitesSelected(selectedUserSite)) {
    return ALL_SITES;
  }

  return null;
}

/**
 * Site id for POST /v1/trial-matches/sites (Find Trials). All Sites is sent as null so the
 * backend enables internal all-sites mode; specific sites send their id.
 */
export function getTrialMatchSiteId(selectedUserSite: SelectedSite): string | null {
  if (!selectedUserSite) {
    return null;
  }

  if (isAllSitesSelected(selectedUserSite)) {
    return null;
  }

  const id = selectedUserSite.id?.trim();
  return id || null;
}

/** True until the user-sites request resolves or fails. A cached selection can still render. */
export function isUserSitesPending(
  userSites: UserSiteMap | null,
  userSitesLoading: boolean,
  siteError: string,
): boolean {
  return userSites == null && (userSitesLoading || siteError === '');
}

export function getSitesArray(sites: UserSiteMap | null): SiteWithId[] {
  return Object.entries(sites || {}).map(([id, values]) => ({
    id,
    name: values.name,
    shortName: values.shortName,
    latestDocumentApplicableAt: values.latestDocumentApplicableAt,
  }));
}
